"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.generateAnalyticsData = generateAnalyticsData;
exports.getAnalyticsData = getAnalyticsData;
exports.getAnalyticsDataV2 = getAnalyticsDataV2;
exports.fetchHistoricalPrices = fetchHistoricalPrices;
const alphafi_sdk_1 = require("@alphafi/alphafi-sdk");
const protocol_js_1 = require("./protocol.js");
const treasury_js_1 = require("./treasury.js");
const alpha_js_1 = require("./alpha.js");
const decimal_js_1 = require("decimal.js");
const index_js_1 = require("../protocol/index.js");
const dailyRevenue_js_1 = require("./crons/dailyRevenue.js");
const getMarkets_js_1 = require("../alphalend/runQueries/getMarkets.js");
const getAddressPortfolio_js_1 = require("../alphalend/runQueries/getAddressPortfolio.js");
const logger_js_1 = require("../logger.js");
// ------------- local mode implementation --------------
const TVL_js_1 = require("./crons/TVL.js");
const prices_js_1 = require("./crons/prices.js");
const dynamoDBClient_js_1 = require("../dynamoDBClient.js");
async function generateAnalyticsData(params) {
    const response = {
        historicalTVL: [],
        dailyRevenue: { total: "" },
        annualisedRevenue: "",
        totalAlphaStakers: 0,
        treasuryTotalHoldingsUsd: "",
        treasuryAlphaHolding: "",
        treasuryHoldingsDistribution: {},
        circulatingAlphaSupply: "",
        maxAlphaSupply: "",
        treasurySharePerAlpha: "",
        airdropApr: "",
        alphaHistoricalMarketCap: [],
    };
    try {
        // Fetch treasury holdings
        async function fetchTreasuryHoldings() {
            const treasuryAddress = (0, alphafi_sdk_1.getConf)().TREASURY_ADDRESS;
            const treasuryHoldings = {
                alphafi: {},
                alphalend: {},
                total: { address: treasuryAddress, totalUsdBalance: "0" },
            };
            const poolNames = Object.keys(alphafi_sdk_1.poolInfo).map((poolName) => poolName);
            let totalUsdBalance = new decimal_js_1.Decimal(0);
            for (const poolName of poolNames) {
                const ten = new decimal_js_1.Decimal(10);
                if (["CETUS", "BLUEFIN"].includes(alphafi_sdk_1.poolInfo[poolName].parentProtocolName)) {
                    const portfolioAmount = await (0, index_js_1.getPortfolioAmount)(poolName, { address: treasuryAddress }, false);
                    const portfolioAmountInUsd = await (0, index_js_1.getPortfolioAmountInUSD)(poolName, { address: treasuryAddress }, false);
                    const { coinA, coinB } = (0, alphafi_sdk_1.coinsInPool)(poolName);
                    if (portfolioAmount &&
                        portfolioAmountInUsd &&
                        portfolioAmountInUsd !== "0") {
                        treasuryHoldings.alphafi[poolName] = {
                            address: treasuryAddress,
                            tokensB: new decimal_js_1.Decimal(portfolioAmount[1])
                                .div(ten.pow(alphafi_sdk_1.coinsList[coinB].expo))
                                .toString(),
                            tokensA: new decimal_js_1.Decimal(portfolioAmount[0])
                                .div(ten.pow(alphafi_sdk_1.coinsList[coinA].expo))
                                .toString(),
                            usdBalance: portfolioAmountInUsd,
                        };
                        totalUsdBalance = totalUsdBalance.add(new decimal_js_1.Decimal(portfolioAmountInUsd));
                    }
                }
                else if (["NAVI", "BUCKET", "ALPHALEND"].includes(alphafi_sdk_1.poolInfo[poolName].parentProtocolName)) {
                    const portfolioAmount = await (0, index_js_1.getSingleAssetPortfolioAmount)(poolName, { address: treasuryAddress }, false);
                    const portfolioAmountInUsd = await (0, index_js_1.getSingleAssetPortfolioAmountInUSD)(poolName, { address: treasuryAddress }, false);
                    const coin = (0, alphafi_sdk_1.coinsInPool)(poolName);
                    if (portfolioAmount &&
                        portfolioAmountInUsd &&
                        portfolioAmountInUsd !== "0") {
                        treasuryHoldings.alphafi[poolName] = {
                            address: treasuryAddress,
                            tokens: new decimal_js_1.Decimal(portfolioAmount)
                                .div(ten.pow(alphafi_sdk_1.coinsList[coin].expo))
                                .toString(),
                            usdBalance: portfolioAmountInUsd,
                        };
                        totalUsdBalance = totalUsdBalance.add(new decimal_js_1.Decimal(portfolioAmountInUsd));
                    }
                }
                else if (["ALPHAFI"].includes(alphafi_sdk_1.poolInfo[poolName].parentProtocolName)) {
                    const portfolioAmount = await (0, index_js_1.getAlphaPortfolioAmount)(poolName, { address: treasuryAddress }, false);
                    const portfolioAmountInUsd = await (0, index_js_1.getAlphaPortfolioAmountInUSD)(poolName, { address: treasuryAddress }, false);
                    if (portfolioAmount &&
                        portfolioAmountInUsd &&
                        portfolioAmountInUsd !== "0") {
                        treasuryHoldings.alphafi[poolName] = {
                            address: treasuryAddress,
                            tokens: new decimal_js_1.Decimal(portfolioAmount).toString(),
                            usdBalance: portfolioAmountInUsd,
                        };
                        totalUsdBalance = totalUsdBalance.add(new decimal_js_1.Decimal(portfolioAmountInUsd));
                    }
                }
                else {
                    (0, logger_js_1.logError)("Unhandled protocol found for pool in treasury holdings", {
                        component: "analytics",
                        operation: "fetchTreasuryHoldings",
                        context: {
                            poolName,
                            protocol: alphafi_sdk_1.poolInfo[poolName].parentProtocolName,
                        },
                    });
                }
                treasuryHoldings.alphafi["TOTAL"] = {
                    address: treasuryAddress,
                    totalUsdBalance: totalUsdBalance.toString(),
                };
            }
            // alphalend holdings
            const alphalendMarkets = await (0, getMarkets_js_1.getAllMarkets)();
            const treasuryAlphalendPortfolio = await (0, getAddressPortfolio_js_1.getAddressPortfolio)(treasuryAddress);
            Object.entries(treasuryAlphalendPortfolio.suppliedAmounts).forEach(([marketId, suppliedAmount]) => {
                const market = alphalendMarkets.find((market) => market.marketId === Number(marketId));
                if (market) {
                    treasuryHoldings.alphalend[market.coinType] = {
                        address: treasuryAddress,
                        tokens: suppliedAmount,
                        usdBalance: new decimal_js_1.Decimal(suppliedAmount)
                            .mul(new decimal_js_1.Decimal(market.price))
                            .toString(),
                    };
                }
            });
            Object.entries(treasuryAlphalendPortfolio.borrowedAmounts).forEach(([marketId, borrowedAmount]) => {
                const market = alphalendMarkets.find((market) => market.marketId === Number(marketId));
                if (market) {
                    treasuryHoldings.alphalend[market.coinType] = {
                        address: treasuryAddress,
                        tokens: `-${borrowedAmount}`,
                        usdBalance: new decimal_js_1.Decimal(borrowedAmount)
                            .mul(new decimal_js_1.Decimal(market.price))
                            .mul(-1)
                            .toString(),
                    };
                }
            });
            treasuryHoldings.total.totalUsdBalance = new decimal_js_1.Decimal(treasuryAlphalendPortfolio.netWorth)
                .add(totalUsdBalance)
                .toString();
            return treasuryHoldings;
        }
        // Fetch Prices
        // {SUI: "100", USDC: "1.00", USDT: "1.00", ...}
        // async function fetchTvlTokens(): Promise<TvlTokens[]> {
        //   const tvl_tokens_url = "https://api.alphafi.xyz/TVLTokens?all=true";
        //   const tvlTokensResponse = await fetch(tvl_tokens_url);
        //   if (!tvlTokensResponse.ok) {
        //     const errorText = await tvlTokensResponse.text();
        //     throw new Error(
        //       `HTTP error! status: ${tvlTokensResponse.status}, response: ${errorText}`,
        //     );
        //   }
        //   const tvlTokens: TvlTokens[] =
        //     (await tvlTokensResponse.json()) as TvlTokens[];
        //   // keep only end of day values
        //   const filteredTvlTokens = tvlTokens
        //     .filter((o) => {
        //       return getEndOfDayGMT(Number(o.time)) === Number(o.time);
        //     })
        //     .map((o) => {
        //       return {
        //         ...o,
        //         time: Number(o.time),
        //         poolName: o.poolName as PoolName,
        //         coin: o.coin as CoinName,
        //       };
        //     });
        //   // ALPHA TVL is missing sometimes due to event inconsistency, this is a temporary patch. should be removed.
        //   function temporaryAlphaInconsistency() {
        //     const alphaItems = filteredTvlTokens
        //       .filter((i) => i.poolName === "ALPHA")
        //       .sort((a, b) => a.time - b.time);
        //     let lastTvl: string = "0";
        //     for (const i of alphaItems) {
        //       if (i.amount === "0") i.amount = lastTvl;
        //       if (i.amount) lastTvl = i.amount;
        //     }
        //     return alphaItems;
        //   }
        //   const patchedAlphaTvlTokens = temporaryAlphaInconsistency();
        //   const patchedTvlTokens = filteredTvlTokens
        //     .filter((i) => i.poolName !== "ALPHA")
        //     .concat(patchedAlphaTvlTokens)
        //     .sort((a, b) => a.time - b.time);
        //   return patchedTvlTokens;
        // }
        // ------------- local mode implementation --------------
        async function fetchTvlTokens() {
            const dbData = await (0, TVL_js_1.getTVLDataFromDynamoDB)({
                tableName: "TVLTokensTable",
                ddbClient: (0, dynamoDBClient_js_1.getDynamoDBClient)(),
            });
            const TVLTokens = dbData.map((entry) => {
                const object = {
                    poolName: entry.poolName_coin.S
                        ? entry.poolName_coin.S.split("#")[0]
                        : "",
                    coin: entry.poolName_coin.S
                        ? entry.poolName_coin.S.split("#")[1]
                        : "",
                    amount: entry.amount.S ? entry.amount.S : "",
                    time: entry.time.N ? entry.time.N : "",
                };
                if (Object.values(object).includes("")) {
                    (0, logger_js_1.logError)("Expected data not found in DB entry", {
                        component: "analytics",
                        operation: "fetchTvlTokens",
                        context: { entry },
                    });
                    throw new Error("expected data not found in the DB");
                }
                return object;
            });
            // keep only end of day values
            const filteredTvlTokens = TVLTokens.filter((o) => {
                return (0, dailyRevenue_js_1.getEndOfDayGMT)(Number(o.time)) === Number(o.time);
            }).map((o) => {
                return {
                    ...o,
                    time: Number(o.time),
                    poolName: o.poolName,
                    coin: o.coin,
                };
            });
            // ALPHA TVL is missing sometimes due to event inconsistency, this is a temporary patch. should be removed.
            function temporaryAlphaInconsistency() {
                const alphaItems = filteredTvlTokens
                    .filter((i) => i.poolName === "ALPHA")
                    .sort((a, b) => a.time - b.time);
                let lastTvl = "0";
                for (const i of alphaItems) {
                    if (i.amount === "0")
                        i.amount = lastTvl;
                    if (i.amount)
                        lastTvl = i.amount;
                }
                return alphaItems;
            }
            const patchedAlphaTvlTokens = temporaryAlphaInconsistency();
            const patchedTvlTokens = filteredTvlTokens
                .filter((i) => i.poolName !== "ALPHA")
                .concat(patchedAlphaTvlTokens)
                .sort((a, b) => a.time - b.time);
            return patchedTvlTokens;
        }
        // ------------------------------------------------------
        async function hardcodedTreasuryHoldings() {
            const treasuryAddress = (0, alphafi_sdk_1.getConf)().TREASURY_ADDRESS;
            const [alphaPriceStr, stsuiPriceStr] = await (0, alphafi_sdk_1.getLatestPrices)(["ALPHA/USD", "STSUI/USD"], false);
            const alphaPrice = Number(alphaPriceStr);
            const stsuiPrice = Number(stsuiPriceStr);
            const alphafiAlphaTokens = 88700.68;
            const alphalendAlphaTokens = 251126.34;
            const alphalendStsuiTokens = 22770.07;
            return {
                alphafi: {
                    ALPHA: {
                        address: treasuryAddress,
                        tokens: alphafiAlphaTokens.toString(),
                        usdBalance: (alphafiAlphaTokens * alphaPrice).toString(),
                    },
                },
                alphalend: {
                    "0xfe3afec26c59e874f3c1d60b8203cb3852d2bb2aa415df9548b8d688e6683f93::alpha::ALPHA": {
                        address: treasuryAddress,
                        tokens: alphalendAlphaTokens.toString(),
                        usdBalance: (alphalendAlphaTokens * alphaPrice).toString(),
                    },
                    "0xd1b72982e40348d069bb1ff701e634c117bb5f741f44dff91e472d3b01461e55::stsui::STSUI": {
                        address: treasuryAddress,
                        tokens: alphalendStsuiTokens.toString(),
                        usdBalance: (alphalendStsuiTokens * stsuiPrice).toString(),
                    },
                },
                total: {
                    address: treasuryAddress,
                    totalUsdBalance: (alphafiAlphaTokens * alphaPrice +
                        alphalendAlphaTokens * alphaPrice +
                        alphalendStsuiTokens * stsuiPrice).toString(),
                },
            };
        }
        const [treasuryHoldings, alphaHoldings, prices, dailyRevenue, tvlTokens, alphaHistoricalMarketCap,] = await Promise.all([
            // fetchTreasuryHoldings(),
            hardcodedTreasuryHoldings(),
            params.alphaHoldersCallback(),
            (0, alphafi_sdk_1.fetchRequiredPrices)(),
            (0, protocol_js_1.getDailyRevenue)(),
            fetchTvlTokens(),
            (0, alpha_js_1.getAlphaHistoricalMarketCap)(),
        ]);
        // Calculate daily revenue
        response.dailyRevenue = dailyRevenue;
        // Calculate annualised revenue
        response.annualisedRevenue = await (0, protocol_js_1.getAnnualisedRevenue)({
            dailyRevenue: dailyRevenue.total,
        });
        // Calculate total users
        if (!alphaHoldings)
            throw new Error("alpha holders not found");
        response.totalAlphaStakers = await (0, protocol_js_1.getTotalAlphaStakers)({ alphaHoldings });
        // Calculate treasury total holdings USD
        response.treasuryTotalHoldingsUsd = await (0, treasury_js_1.getTotalHoldingsUsd)({
            treasuryHoldings,
        });
        // Check ALPHA price exists
        if (!prices["ALPHA"])
            throw new Error("Price of ALPHA not found");
        // Get treasury ALPHA holding
        response.treasuryAlphaHolding = await (0, treasury_js_1.getAlphaHeld)({ treasuryHoldings });
        // Get treasury holdings distribution
        response.treasuryHoldingsDistribution = await (0, treasury_js_1.getHoldingsDistribution)({
            treasuryHoldings,
            prices,
        });
        // Set circulating supply of ALPHA
        response.circulatingAlphaSupply = await (0, alpha_js_1.getCirculatingSupply)();
        // Get staked ALPHA supply
        response.maxAlphaSupply = "10000000";
        // Get treasury share per ALPHA
        response.treasurySharePerAlpha = await (0, alpha_js_1.getTreasurySharePerAlpha)({
            treasuryTotalHoldingsUsd: response.treasuryTotalHoldingsUsd,
            circulatingAlphaSupply: response.circulatingAlphaSupply,
        });
        // Get alpha historical market cap
        response.alphaHistoricalMarketCap = alphaHistoricalMarketCap;
        // Calculate airdrop APR
        response.airdropApr = await (0, alpha_js_1.getAirdropApr)({
            annualisedRevenue: response.annualisedRevenue,
            alphaPrice: prices["ALPHA"],
        });
        // Get Current TVL
        // console.time("latestTvl");
        // response.latestTVL = await getLatestTVL();
        // console.timeEnd("latestTvl");
        // Get Historical TVL
        response.historicalTVL = await (0, protocol_js_1.getHistoricalTVL)({ tvlTokens });
    }
    catch (error) {
        (0, logger_js_1.logError)("Failed to generate analytics data", {
            component: "analytics",
            operation: "generateAnalyticsData",
            error: error instanceof Error ? error : new Error(String(error)),
        });
        throw error;
    }
    return response;
}
async function getAnalyticsData() {
    const stats_url = `https://api.alphafi.xyz/stats`;
    const statsResponse = await fetch(stats_url);
    if (!statsResponse.ok) {
        const errorText = await statsResponse.text();
        throw new Error(`HTTP error! status: ${statsResponse.status}, response: ${errorText}`);
    }
    const statsData = await statsResponse.json();
    statsData.latestTVL = await (0, protocol_js_1.getLatestTVL)();
    return statsData;
}
async function getAnalyticsDataV2() {
    const analyticsDataV1 = await getAnalyticsData();
    const analyticsDataV2 = analyticsDataV1;
    // change the treasuryHoldingsDistribution keys
    analyticsDataV2.treasuryHoldingsDistribution = {
        ALPHA: analyticsDataV1.treasuryHoldingsDistribution.ALPHA,
        STSUI: analyticsDataV1.treasuryHoldingsDistribution.Sui,
    };
    // add treasuryStsuiHolding
    const [stsuiPrice] = await (0, alphafi_sdk_1.getLatestPrices)(["STSUI/USD"], false);
    // analyticsDataV2 is reference of analyticsDataV1
    const stsuiHeld = (Number(analyticsDataV1.treasuryHoldingsDistribution.STSUI) *
        Number(analyticsDataV1.treasuryTotalHoldingsUsd)) /
        Number(stsuiPrice) /
        100;
    analyticsDataV2.treasuryStsuiHolding = stsuiHeld.toString();
    return analyticsDataV2;
}
// export async function fetchHistoricalPrices(
//   params: {
//     maxTime: number;
//     minTime: number;
//     coinPairs: string[];
//   },
//   repair: boolean = true,
// ): Promise<HistoricalPrices> {
//   const coinPairs = params.coinPairs.join(",");
//   const historical_prices_url = `https://api.alphafi.xyz/historical-prices?max=${params.maxTime}&min=${params.minTime}&coin-pairs=${coinPairs}`;
//   const historicalPricesResponse = await fetch(historical_prices_url);
//   if (!historicalPricesResponse.ok) {
//     const errorText = await historicalPricesResponse.text();
//     throw new Error(
//       `HTTP error! status: ${historicalPricesResponse.status}, response: ${errorText}, url: ${historical_prices_url}`,
//     );
//   }
//   const historicalPrices: HistoricalPrices =
//     await historicalPricesResponse.json();
//   return historicalPrices;
//   // TODO --> this is temporary patch, move this logic to backend
//   // const checkedHistoricalPrices = await checkHistoricalPrices(
//   //   {
//   //     historicalPrices,
//   //     maxTime: params.maxTime,
//   //     minTime: params.minTime,
//   //   },
//   //   repair,
//   // );
//   // return checkedHistoricalPrices;
// }
// ------- SUBSTITUTE FUNCTION FOR LOCAL TESTING ----------
async function fetchHistoricalPrices(params) {
    const keys = params.coinPairs.map((coinPair) => {
        return { coinPair, minTime: params.minTime, maxTime: params.maxTime };
    });
    const historicalPrices = await (0, prices_js_1.getPricesDataFromHistoricalPricesDynamoDb)({
        tableName: "HistoricalPricesTable",
        priceQuery: keys,
    });
    return historicalPrices;
}
// -----------------------------------------------------------------------------------------------------------------
// CODE WITH ALTERNATE APPROACHES
// this code is out dated, the treasury holdings type has changed
// --------- Optimised fetchTreasuryHoldings function ------------
// TODO --> multi convert functions become outdated, fix them, permanently
// async function fetchTreasuryHoldings(): Promise<TreasuryHoldings> {
//   try {
//     {
//       const treasuryAddress = getConf().TREASURY_ADDRESS;
//       // get the xtokens from receipts
//       const xtokensHoldings: HoldingsObj[] = [];
//       for (const poolName in poolInfo) {
//         const poolReceipts = await getReceipts(
//           poolName as PoolName,
//           treasuryAddress,
//           false,
//         );
//         if (poolReceipts.length === 0) continue;
//         xtokensHoldings.push({
//           owner: treasuryAddress,
//           poolName: poolName as PoolName,
//           holding: poolReceipts
//             .reduce((xtokensTotal: Decimal, receipt: Receipt) => {
//               return xtokensTotal.add(receipt.content.fields.xTokenBalance);
//             }, new Decimal(0))
//             .toString(),
//         });
//       }
//       // get the tokens from xtokens
//       const tokenHoldings = await multiLiquidityToTokens(
//         await multiXTokensToLiquidity(xtokensHoldings),
//       );
//       // get the usd holdings
//       const usdHoldings = await multiTokensToUsd(tokenHoldings);
//       // map to treasury holding object
//       const treasuryHoldings: TreasuryHoldings = {};
//       tokenHoldings.map((tokenHolding) => {
//         const poolName = tokenHolding.poolName;
//         const address = tokenHolding.user;
//         if ("tokens" in tokenHolding) {
//           const usdBalance = {
//             address: address,
//             tokens: tokenHolding.tokens,
//             usdBalance: usdHoldings.find(
//               (usdHolding) => usdHolding.poolName === poolName,
//             )!.holding,
//           };
//           treasuryHoldings[poolName] = usdBalance;
//         } else if ("tokenAmountA" in tokenHolding) {
//           const usdBalance = {
//             address: address,
//             tokensA: tokenHolding.tokenAmountA,
//             tokensB: tokenHolding.tokenAmountB,
//             usdBalance: usdHoldings.find(
//               (usdHolding) => usdHolding.poolName === poolName,
//             )!.holding,
//           };
//           treasuryHoldings[poolName] = usdBalance;
//         } else {
//           console.error("holding: ", tokenHolding);
//           throw new Error("Unexpected holding found");
//         }
//       });
//       treasuryHoldings["TOTAL"] = {
//         address: treasuryAddress,
//         totalUsdBalance: usdHoldings
//           .reduce((total: Decimal, holdingObj: HoldingsObj) => {
//             return total.add(new Decimal(holdingObj.holding));
//           }, new Decimal(0))
//           .toString(),
//       };
//       return treasuryHoldings;
//     }
//   } catch (e) {
//     throw e;
//   }
// }
// Fetch Alpha Holdings
// THIS FUNCTION USES HOLDERS API INTERNALLY TO FETCH NUMBER OF ALPHA HOLDERS
// TODO --> fix holders api with reliable events, currently implemented a sensitive scraping based solution
// async function fetchAlphaHoldings(): Promise<string> {
//   const alpha_holdings_url = `https://api.alphafi.xyz/stats/alphaHolders`;
//   const alphaHoldingsResponse = await fetch(alpha_holdings_url);
//   if (!alphaHoldingsResponse.ok) {
//     const errorText = await alphaHoldingsResponse.text();
//     throw new Error(
//       `HTTP error! status: ${alphaHoldingsResponse.status}, response: ${errorText}`,
//     );
//   }
//   const alphaHoldings: string = await alphaHoldingsResponse.json();
//   return alphaHoldings;
// }
// ------------------------------------------------------------------------------------------------------------
//# sourceMappingURL=index.js.map